import { useQuery } from '@tanstack/react-query';
import { api, type ChatConversationSearchHit } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/** Fewest characters before the server is asked (matches the route's validation). */
export const CHAT_CONVERSATION_SEARCH_MIN_LENGTH = 2;

/**
 * Server-side search over the user's NAMED conversations by name — backed by
 * `GET /arches/chat/channels/search`, so a channel outside the loaded first
 * page of the chat list is still found. The caller is responsible for
 * debouncing `query`; the hook does not, so the panel's one debounce governs
 * every section. Idle (no request, no loading state) below the minimum length
 * or while disabled.
 */
export function useChatConversationSearch(
  query: string,
  enabled: boolean,
): {
  results: ChatConversationSearchHit[];
  /** True when the query is long enough to be sent (the panel's own gate too). */
  isSearchable: boolean;
  isLoading: boolean;
  isError: boolean;
} {
  const trimmed = query.trim();
  const searchable = trimmed.length >= CHAT_CONVERSATION_SEARCH_MIN_LENGTH;

  const result = useQuery({
    queryKey: queryKeys.chat.conversationSearch(trimmed),
    queryFn: () => api.searchChatChannels(trimmed),
    select: (data) => data.channels,
    enabled: enabled && searchable,
    staleTime: 30_000,
  });

  return {
    results: result.data ?? [],
    isSearchable: searchable,
    isLoading: enabled && searchable && result.isLoading,
    isError: result.isError,
  };
}
